/**
 * An estimate's materials from the 3D designer's document.
 *
 * Once the designer has been used on an estimate, what the customer sees on the
 * results page should be what they laid out — every room of the plan, with its
 * sections, SlatWall inserts, windows and doors — not the wizard's plain room
 * the plan was seeded from. This takes the wizard's `EstimateOutput` and
 * replaces its bill of materials, totals and per-surface breakdown with the
 * plan's, leaving everything else (input summary, project info, id, currency,
 * notes) as it was, plus one note saying where the materials came from.
 *
 * The bill of materials is the PLAN's (`calculateFromPlan`: one SlatWall offcut
 * pool across the whole project, as the engine does across walls). The
 * per-surface breakdown is informational — each surface counted on its own —
 * so its panel counts can add up to slightly more than the bill, where an
 * offcut from one surface was used on another.
 */

import { CeilingConfig, EstimateOutput, MaterialItem, SurfaceBreakdown, WallConfig } from '../../types';
import { Surface } from '../../types/sections';
import { calculateFromSurfaces } from '../sections/calculateFromSurfaces';
import { FloorPlan, PlanRoom } from './floorPlan';
import { calculateFromPlan } from './planEstimate';

export const DESIGNER_NOTE_KEY = 'results:notes.designerLayout';
export const DESIGNER_NOTE_TEXT = 'Materials are from the 3D Designer layout';

const NO_SLATWALL = { enabled: false, startHeight: 0, numPanelRows: 0, color: 'White' as const, useCapTrim: false };
const NO_GARAGE = { enabled: false, width: 0, height: 0, numDoors: 0 };

/** The colour most of a surface is: its widest section's, else white. */
function mainColor(surface: Surface): 'White' | 'Gray' {
  const widest = [...surface.sections].sort((a, b) => b.width * b.height - a.width * a.height)[0];
  return widest?.color ?? 'White';
}

/** A surface's own materials (panels and trims), counted on its own. */
function surfaceMaterials(surface: Surface): { panels: MaterialItem[]; trims: MaterialItem[] } {
  const est = surface.kind === 'ceiling'
    ? calculateFromSurfaces({ walls: [], ceiling: surface })
    : calculateFromSurfaces({ walls: [surface] });
  const b = est.billOfMaterials;
  return {
    panels: [...b.wallAndCeilingBoard, ...b.slatwall],
    trims: [...b.hTrims, ...(b.jTrimsSnapIn ?? []), ...(b.otherTrims ?? [])],
  };
}

function wallBreakdown(room: PlanRoom, surface: Surface, several: boolean): SurfaceBreakdown {
  const config: WallConfig = {
    wallId: surface.id,
    name: surface.name,
    enabled: true,
    length: surface.width,
    height: surface.height,
    color: mainColor(surface),
    orientation: surface.sections[0]?.orientation ?? 'Horizontal',
    slatWall: NO_SLATWALL,
    garage: NO_GARAGE,
  };
  return {
    surfaceId: `${room.id}:${surface.id}`,
    surfaceName: several ? `${room.name} · ${surface.name}` : surface.name,
    enabled: true,
    config,
    materials: surfaceMaterials(surface),
  };
}

function ceilingBreakdown(room: PlanRoom, surface: Surface, several: boolean): SurfaceBreakdown {
  const config: CeilingConfig = { enabled: true, color: mainColor(surface), orientation: surface.sections[0]?.orientation ?? 'Horizontal' };
  return {
    surfaceId: `${room.id}:${surface.id}`,
    surfaceName: several ? `${room.name} · ${surface.name}` : surface.name,
    enabled: true,
    config,
    materials: surfaceMaterials(surface),
  };
}

/** The per-surface breakdown of a plan: every wall of every room, then the ceilings. */
export function planSurfaceBreakdown(plan: FloorPlan): EstimateOutput['surfaces'] {
  const several = plan.rooms.length > 1;
  const walls = plan.rooms.flatMap((room) => room.surfaces.walls.map((w) => wallBreakdown(room, w, several)));
  const ceilings = plan.rooms.flatMap((room) => (room.surfaces.ceiling ? [ceilingBreakdown(room, room.surfaces.ceiling, several)] : []));
  return {
    walls,
    ceiling: ceilings.length === 1 ? ceilings[0] : undefined,
    ceilingZones: ceilings.length > 1 ? ceilings : undefined,
  };
}

/**
 * The wizard's estimate output with its materials replaced by the plan's.
 * Pure: a new object; `output` is not changed.
 */
export function withDesignerMaterials(output: EstimateOutput, plan: FloorPlan): EstimateOutput {
  const est = calculateFromPlan(plan);
  const notes = output.notes.includes(DESIGNER_NOTE_TEXT) ? output.notes : [...output.notes, DESIGNER_NOTE_TEXT];
  const notesI18n = output.notesI18n
    ? (output.notesI18n.some((n) => n.key === DESIGNER_NOTE_KEY) ? output.notesI18n : [...output.notesI18n, { key: DESIGNER_NOTE_KEY, text: DESIGNER_NOTE_TEXT }])
    : undefined;
  return {
    ...output,
    surfaces: planSurfaceBreakdown(plan),
    billOfMaterials: est.billOfMaterials,
    totals: est.totals,
    notes,
    notesI18n,
  };
}
